import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, Users, CheckCircle, AlertCircle, ArrowRight, X, Loader2, Database, Trash2, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminCustomerImport() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [token, setToken] = useState(null);
  const [mode, setMode] = useState("merge");
  const [grantWelcome, setGrantWelcome] = useState(true);
  const [grantCashback, setGrantCashback] = useState(false);
  const [replacePurchases, setReplacePurchases] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState(null);

  const reset = () => {
    setFile(null); setPreview(null); setToken(null); setError("");
    setCommitResult(null);
    setClearResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = (f) => {
    if (!f) return;
    setError("");
    setPreview(null); setToken(null); setCommitResult(null);
    setFile(f);
  };

  const doPreview = async () => {
    if (!file) return;
    setLoading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/customers/preview`, fd, { withCredentials: true });
      setPreview(r.data);
      setToken(r.data.token);
    } catch (e) {
      setError(e?.response?.data?.detail || "Не удалось обработать файл");
    } finally { setLoading(false); }
  };

  const doCommit = async () => {
    if (!token) return;
    setCommitting(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/customers/commit`,
        { token, mode, grant_welcome: grantWelcome, grant_cashback: grantCashback, replace_purchases: replacePurchases },
        { withCredentials: true });
      setCommitResult(r.data);
      setPreview(null); setToken(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка импорта");
    } finally { setCommitting(false); }
  };

  const doClearImported = async () => {
    if (!window.confirm("Удалить ВСЕХ импортированных клиентов и их покупки? Это действие необратимо. Тестовые/сидовые клиенты сохранятся.")) return;
    setClearing(true); setError(""); setClearResult(null);
    try {
      const r = await axios.post(`${API}/admin/customers/clear-imported`, {}, { withCredentials: true });
      setClearResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Не удалось удалить импортированных клиентов");
    } finally { setClearing(false); }
  };

  return (
    <div className="light-section" data-testid="admin-customers-import-page" style={{ maxWidth: "900px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Database size={22} style={{ color: "#C5A059" }} />
          <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", margin: 0, fontWeight: 500 }}>Импорт клиентской базы</h2>
        </div>
        <button data-testid="clear-imported-btn" onClick={doClearImported} disabled={clearing}
          style={{ background: "white", border: "1px solid #fecaca", color: "#dc2626", borderRadius: "6px", padding: "6px 12px", cursor: clearing ? "wait" : "pointer", fontFamily: "Manrope", fontSize: "11px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: "6px" }}>
          {clearing ? <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} /> : <Trash2 size={12} />}
          Очистить импортированных
        </button>
      </div>
      <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#6b7280", lineHeight: 1.6, marginBottom: "20px" }}>
        Скачайте <b>шаблон Excel</b> — там уже стоят правильные заголовки колонок (имя, телефон, дата покупки, тип изделия, бренд, мех, сумма и т.д.) + пример-строка + инструкция по заполнению. Заполните и загрузите обратно.
        <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
          <a href={`${API}/admin/customers/template.xlsx`} target="_blank" rel="noreferrer"
             data-testid="download-template-btn"
             style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 14px",
                       background: "#0A0A0A", color: "#C5A059", textDecoration: "none",
                       borderRadius: "6px", fontFamily: "Manrope", fontSize: "12px",
                       fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                       border: "1px solid #C5A059" }}>
            <Download size={13} /> Скачать шаблон .xlsx
          </a>
          <span style={{ fontSize: "12px", color: "#94a3b8" }}>
            Формат: .xlsx / .csv. Одна строка = одна покупка. Дозаливка не дублирует.
          </span>
        </div>
      </div>

      {clearResult && (
        <div data-testid="clear-result" style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: "8px", padding: "12px 14px", marginBottom: "20px", color: "#991b1b", fontFamily: "Manrope", fontSize: "12.5px" }}>
          Удалено: <b>{clearResult.deleted_users}</b> клиентов, <b>{clearResult.deleted_purchases}</b> покупок, <b>{clearResult.deleted_bonus_tx}</b> бонусных транзакций. Теперь можно перезалить файл с нуля.
        </div>
      )}

      {!preview && !commitResult && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "28px" }}>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.xlsm" style={{ display: "none" }}
            data-testid="customers-file-input"
            onChange={(e) => onFile(e.target.files?.[0])} />
          <div onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
            data-testid="customers-dropzone"
            style={{ border: "2px dashed #C5A059", borderRadius: "10px", padding: "36px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", cursor: "pointer", background: file ? "rgba(197,160,89,0.05)" : "#FDFBF7" }}>
            {file ? (
              <>
                <Users size={36} style={{ color: "#0A8043" }} />
                <div style={{ fontFamily: "Manrope", fontSize: "14px", color: "#0A0A0A", fontWeight: 600 }}>{file.name}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#6b7280" }}>{(file.size / 1024).toFixed(0)} КБ</div>
              </>
            ) : (
              <>
                <Upload size={32} style={{ color: "#C5A059" }} />
                <div style={{ fontFamily: "Manrope", fontSize: "14px", color: "#0A0A0A", fontWeight: 600 }}>Выберите заполненный файл</div>
                <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#6b7280" }}>.xlsx или .csv — по формату скачанного шаблона</div>
              </>
            )}
          </div>

          {error && <div style={{ marginTop: "14px", padding: "10px 14px", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: "6px", color: "#dc2626", fontFamily: "Manrope", fontSize: "12px" }}>{error}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px", gap: "10px" }}>
            {file && <button onClick={reset} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: "13px", color: "#6b7280" }}>Отмена</button>}
            <button data-testid="customers-preview-btn" onClick={doPreview} disabled={!file || loading}
              style={{ background: file ? "linear-gradient(135deg,#C5A059,#D4B878)" : "#e5e7eb", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "10px 18px", cursor: file ? "pointer" : "default", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px" }}>
              {loading ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <ArrowRight size={14} />}
              {loading ? "Обработка..." : "Предпросмотр"}
            </button>
          </div>
        </div>
      )}

      {preview && (
        <div data-testid="customers-preview" style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", margin: 0, fontWeight: 500 }}>Распознанные данные</h3>
            <button onClick={reset} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={18} /></button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "10px", marginBottom: "16px" }}>
            {[
              { l: "Всего", v: preview.summary.total, c: "#0A0A0A" },
              { l: "Создать", v: preview.summary.to_create, c: "#0A8043" },
              { l: "Обновить", v: preview.summary.to_update, c: "#C5A059" },
              { l: "Без телефона", v: preview.summary.no_phone, c: "#dc2626" },
            ].map(s => (
              <div key={s.l} style={{ background: "#FDFBF7", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid #f3f4f6" }}>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", fontWeight: 600, color: s.c }}>{s.v}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "2px" }}>{s.l}</div>
              </div>
            ))}
          </div>

          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px", fontWeight: 700 }}>Найденные колонки</div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "16px" }}>
            {Object.entries(preview.detected_mapping).map(([field, idx]) => (
              <span key={field} style={{ background: "#fef3c7", color: "#92400e", fontFamily: "Manrope", fontSize: "11px", padding: "4px 10px", borderRadius: "12px", fontWeight: 600 }}>
                {field}: «{preview.headers[idx] || `#${idx + 1}`}»
              </span>
            ))}
          </div>

          <div style={{ maxHeight: "320px", overflowY: "auto", border: "1px solid #f3f4f6", borderRadius: "6px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Manrope", fontSize: "12px" }}>
              <thead style={{ background: "#FDFBF7", position: "sticky", top: 0 }}>
                <tr>
                  <th style={th}>Имя</th>
                  <th style={th}>Телефон</th>
                  <th style={{ ...th, textAlign: "center" }}>Покупок</th>
                  <th style={{ ...th, textAlign: "right" }}>Сумма ₸</th>
                  <th style={th}>Действие</th>
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((row, idx) => (
                  <tr key={idx} style={{ borderTop: "1px solid #f3f4f6" }}>
                    <td style={td}>{row.name || <span style={{ color: "#9ca3af" }}>—</span>}</td>
                    <td style={td}>{row.phone || <span style={{ color: "#dc2626" }}>{row.phone_raw || "—"}</span>}</td>
                    <td style={{ ...td, textAlign: "center", color: "#6b7280" }}>{row.purchases_count ?? "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{row.total_spent ? row.total_spent.toLocaleString("ru-RU") : "—"}</td>
                    <td style={td}>
                      {!row.phone ? (
                        <span style={{ color: "#dc2626", fontWeight: 600 }}>Пропустить</span>
                      ) : row.match === "update" ? (
                        <span style={{ color: "#C5A059", fontWeight: 600 }}>Обновить</span>
                      ) : (
                        <span style={{ color: "#0A8043", fontWeight: 600 }}>Создать</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: "20px", padding: "14px", background: "#FDFBF7", borderRadius: "6px", border: "1px solid #f3f4f6" }}>
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Режим импорта</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
              {[
                { v: "merge", l: "Создать и обновить" },
                { v: "create_only", l: "Только новые" },
                { v: "update_only", l: "Только обновить" },
              ].map(o => (
                <label key={o.v} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px", background: mode === o.v ? "rgba(197,160,89,0.15)" : "white", border: `1px solid ${mode === o.v ? "#C5A059" : "#e5e7eb"}`, borderRadius: "6px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px" }}>
                  <input type="radio" name="mode" value={o.v} checked={mode === o.v} onChange={() => setMode(o.v)} style={{ accentColor: "#C5A059" }} />
                  {o.l}
                </label>
              ))}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer" }}>
              <input type="checkbox" checked={grantWelcome} onChange={(e) => setGrantWelcome(e.target.checked)} style={{ accentColor: "#C5A059" }} />
              Начислить welcome-бонус <b style={{ color: "#C5A059" }}>10 000 ₸</b> новым клиентам
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer", marginTop: "6px" }}>
              <input data-testid="grant-cashback-cb" type="checkbox" checked={grantCashback} onChange={(e) => setGrantCashback(e.target.checked)} style={{ accentColor: "#C5A059" }} />
              Начислить кэшбэк за импортированные покупки (1%/2%/3%/4% по тиру, заблокирован 15 дней)
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer", marginTop: "6px", padding: "8px 10px", background: replacePurchases ? "rgba(220,38,38,0.06)" : "transparent", borderRadius: "6px", border: replacePurchases ? "1px solid rgba(220,38,38,0.2)" : "1px solid transparent" }}>
              <input data-testid="replace-purchases-cb" type="checkbox" checked={replacePurchases} onChange={(e) => setReplacePurchases(e.target.checked)} style={{ accentColor: "#dc2626" }} />
              <span><b style={{ color: "#dc2626" }}>Перезаписать данные:</b> для существующих клиентов удалить все ранее импортированные покупки и заменить суммой из этого файла (используйте при повторной загрузке свежей выгрузки). Если не отмечено — дубликаты определяются по дате+сумме+товару и пропускаются.</span>
            </label>
          </div>

          {error && <div style={{ marginTop: "12px", color: "#dc2626", fontFamily: "Manrope", fontSize: "12px" }}>{error}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px", gap: "10px" }}>
            <button onClick={reset} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: "13px", color: "#6b7280" }}>Отмена</button>
            <button data-testid="customers-commit-btn" onClick={doCommit} disabled={committing}
              style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "10px 18px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px" }}>
              {committing ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <CheckCircle size={14} />}
              {committing ? "Импортируется..." : "Импортировать"}
            </button>
          </div>
        </div>
      )}

      {commitResult && (
        <div data-testid="customers-import-result" style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
            <CheckCircle size={24} style={{ color: "#0A8043" }} />
            <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", margin: 0 }}>Импорт завершён</h3>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "10px", marginBottom: "16px" }}>
            {[
              { l: "Создано", v: commitResult.created, c: "#0A8043" },
              { l: "Обновлено", v: commitResult.updated, c: "#C5A059" },
              { l: "Покупок добавлено", v: commitResult.purchases_imported || 0, c: "#0A0A0A" },
              { l: "Дубликаты пропущены", v: commitResult.purchases_skipped_duplicate || 0, c: "#6b7280" },
            ].map(s => (
              <div key={s.l} style={{ background: "#FDFBF7", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid #f3f4f6" }}>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", fontWeight: 600, color: s.c }}>{s.v}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "2px" }}>{s.l}</div>
              </div>
            ))}
          </div>
          {(commitResult.purchases_skipped_duplicate || 0) > 0 && (
            <div style={{ marginBottom: "12px", padding: "10px 14px", background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "6px", color: "#4338ca", fontFamily: "Manrope", fontSize: "12.5px" }}>
              ℹ️ Пропущено <b>{commitResult.purchases_skipped_duplicate}</b> покупок-дубликатов (та же дата + сумма + товар уже импортированы ранее). Это защита от удвоения при повторной загрузке.
            </div>
          )}
          {commitResult.bonus_credited > 0 && (
            <div style={{ marginBottom: "12px", padding: "10px 14px", background: "rgba(197,160,89,0.1)", border: "1px solid rgba(197,160,89,0.3)", borderRadius: "6px", color: "#92400e", fontFamily: "Manrope", fontSize: "13px" }}>
              ✨ Начислено кэшбэка на сумму <b>{commitResult.bonus_credited.toLocaleString("ru-RU")} ₸</b> (заблокирован 15 дней)
            </div>
          )}
          {commitResult.errors > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#dc2626", fontFamily: "Manrope", fontSize: "12px", marginBottom: "12px" }}>
              <AlertCircle size={14} /> Часть строк не импортирована. Проверьте логи бэкенда.
            </div>
          )}
          <button onClick={reset}
            style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "10px 18px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Импортировать ещё один файл
          </button>
        </div>
      )}

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const th = { padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: "11px", color: "#6b7280", letterSpacing: "0.06em", textTransform: "uppercase" };
const td = { padding: "8px 12px", color: "#0A0A0A" };
